"use client";

import { useState, type SVGProps } from "react";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { FaInstagram, FaLinkedin, FaTiktok } from "react-icons/fa";

import { cn } from "@/lib/utils";
import { LeadCaptureModal } from "@/components/lead-capture-modal";

// Inline X logo — react-icons/fa predates the X rebrand and the
// Simple Icons (/si) export was pulled over trademark concerns.
// Path is the canonical Simple Icons X glyph.
function IconX(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

type FooterLink = {
  label: string;
  href: string;
  disabled?: boolean;
  external?: boolean;
  isDemo?: boolean;
  badge?: string;
};

type FooterColumn = {
  heading: string;
  links: FooterLink[];
};

// Links with `disabled: true` are hidden until the underlying route
// ships. Flip the flag off (or delete it) to reveal — no other
// changes needed.
const FOOTER_COLS: FooterColumn[] = [
  {
    heading: "Platform",
    links: [
      { label: "How It Works", href: "/#how" },
      { label: "Security", href: "/security" },
      { label: "Pricing", href: "/pricing" },
      { label: "Integrations", href: "/integrations" },
      { label: "Solutions", href: "/solutions" },
      { label: "ROI Calculator", href: "/roi", badge: "New" },
      { label: "Solution Finder", href: "/solution-finder", disabled: true },
      { label: "Business Audit", href: "/business-audit", disabled: true },
      { label: "Get an AI Call", href: "/ai-call", disabled: true },
    ],
  },
  {
    heading: "Company",
    links: [
      { label: "About", href: "/about" },
      { label: "Careers", href: "/careers" },
      { label: "Press & Media", href: "/press", disabled: true },
      { label: "Brand Assets", href: "/brand", disabled: true },
      { label: "Referral Program", href: "/referral", disabled: true },
      { label: "Book a Demo", href: "#", isDemo: true },
      { label: "Sign in", href: "/sign-in" },
    ],
  },
  {
    heading: "Resources",
    links: [
      { label: "Blog", href: "/blog" },
      { label: "Documentation", href: "/docs" },
      { label: "Raileon vs Hiring In-House", href: "/compare/hiring-in-house" },
      {
        label: "Raileon vs a Marketing Agency",
        href: "/compare/marketing-agency",
      },
      { label: "Free Guides", href: "/guides", disabled: true },
      { label: "Competitor Intel", href: "/competitor-spy", disabled: true },
      { label: "Changelog", href: "/changelog", disabled: true },
      { label: "Status", href: "/status", disabled: true },
    ],
  },
  {
    heading: "Legal",
    links: [
      { label: "Terms of Service", href: "/terms" },
      { label: "Privacy Policy", href: "/privacy" },
      { label: "Acceptable Use", href: "/acceptable-use" },
      { label: "AI Transparency", href: "/ai-disclosure" },
      {
        label: "Client Agreement",
        href: "/client-agreement",
        disabled: true,
      },
      {
        label: "Report Security Issue",
        href: "mailto:security@raileon.com",
        external: true,
      },
      {
        label: "Press Inquiries",
        href: "mailto:press@raileon.com",
        external: true,
      },
      { label: "Contact", href: "mailto:admin@raileon.com", external: true },
    ],
  },
];

// Rendered in the bottom bar next to the copyright, not as a column — see the
// comment at the render site.
const SECONDARY_LEGAL: { label: string; href: string }[] = [
  { label: "Cookies", href: "/cookies" },
  { label: "Sub-processors", href: "/subprocessors" },
  { label: "DPA", href: "/dpa" },
  { label: "Refunds", href: "/refunds" },
  { label: "Accessibility", href: "/accessibility" },
];

const SOCIAL_LINKS = [
  {
    label: "Instagram",
    href: "https://www.instagram.com/raileonai",
    Icon: FaInstagram,
  },
  {
    label: "TikTok",
    href: "https://www.tiktok.com/@raileonai",
    Icon: FaTiktok,
  },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/in/raileon-ai-866058400/",
    Icon: FaLinkedin,
  },
  { label: "X", href: "https://x.com/RaileonAi", Icon: IconX },
] as const;

function NewsletterForm({ onGetStarted }: { onGetStarted: () => void }) {
  const [email, setEmail] = useState("");

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    // There's no standalone newsletter store yet, and accounts are
    // invite-only, so instead of faking a subscription ("You're on the
    // list") we open the inquiry form. The form has no prefill yet, so
    // the typed email isn't carried over.
    onGetStarted();
  }

  return (
    <form onSubmit={onSubmit} className="flex w-full max-w-80">
      <label htmlFor="newsletter-email" className="sr-only">
        Email address
      </label>
      <div className="bg-rl-bg3 border-rl-border focus-within:border-rl-accent/40 flex w-full items-center rounded-full border pr-1 pl-4 transition-colors">
        <input
          id="newsletter-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          aria-label="Email address"
          className="text-foreground placeholder:text-rl-text3 flex-1 bg-transparent py-2.5 text-[0.8125rem] outline-none"
        />
        <button
          type="submit"
          aria-label="Get started"
          className="bg-rl-accent hover:bg-rl-accent-bright flex size-8 items-center justify-center rounded-full text-[#08090c] transition-colors"
        >
          <ArrowRight size={14} />
        </button>
      </div>
    </form>
  );
}

function SocialIcons() {
  return (
    <div className="flex flex-wrap gap-2">
      {SOCIAL_LINKS.map((s) => (
        <Link
          key={s.label}
          href={s.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={s.label}
          className="border-rl-border text-rl-text2 hover:text-rl-accent hover:border-rl-accent/50 hover:bg-rl-bg3 flex size-10 items-center justify-center rounded-lg border transition-colors"
        >
          <s.Icon className="size-4" aria-hidden />
        </Link>
      ))}
    </div>
  );
}

export function Footer() {
  const [demoOpen, setDemoOpen] = useState(false);
  const [year] = useState(() => new Date().getFullYear());

  return (
    <footer className="border-rl-border bg-rl-bg2 relative border-t pt-20 pb-10">
      <div className="mx-auto max-w-280 px-6 md:px-8">
        <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-[1.6fr_1fr_1fr_1fr_1fr]">
          <div className="sm:col-span-2 lg:col-span-1">
            <Link
              href="/"
              className="text-foreground mb-4 inline-block text-[1.5rem] font-extrabold tracking-[-0.03em]"
            >
              raileon<span className="text-rl-accent">.</span>
            </Link>
            <p className="text-rl-text2 mb-6 max-w-80 text-[0.8125rem] leading-[1.7]">
              AI employees for your business. Fully managed. Always on.
            </p>
            <div className="mb-6">
              <p className="text-rl-text3 mb-2.5 text-[0.7rem] font-bold tracking-[0.12em] uppercase">
                Get started
              </p>
              <NewsletterForm onGetStarted={() => setDemoOpen(true)} />
            </div>
            <SocialIcons />
          </div>

          {FOOTER_COLS.map((col) => {
            const visible = col.links.filter((l) => !l.disabled);
            if (visible.length === 0) return null;
            return (
              <div key={col.heading}>
                <h4 className="text-rl-text3 mb-4 text-[0.7rem] font-bold tracking-[0.12em] uppercase">
                  {col.heading}
                </h4>
                <div className="flex flex-col gap-2.5">
                  {visible.map((link) => {
                    const content = (
                      <>
                        {link.label}
                        {link.badge && (
                          <span className="bg-rl-accent/15 text-rl-accent ml-1.5 rounded px-1.5 py-[1px] text-[0.625rem] font-extrabold tracking-wider uppercase">
                            {link.badge}
                          </span>
                        )}
                      </>
                    );
                    if (link.isDemo) {
                      return (
                        <button
                          key={link.label}
                          type="button"
                          onClick={() => setDemoOpen(true)}
                          className="text-rl-text2 hover:text-rl-accent w-fit cursor-pointer text-left text-[0.8125rem] transition-colors"
                        >
                          {content}
                        </button>
                      );
                    }
                    return (
                      <Link
                        key={link.label}
                        href={link.href}
                        {...(link.external
                          ? { target: "_blank", rel: "noopener noreferrer" }
                          : {})}
                        className={cn(
                          "text-rl-text2 hover:text-rl-accent w-fit text-[0.8125rem] transition-colors",
                        )}
                      >
                        {content}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="border-rl-border mt-16 flex flex-col items-start justify-between gap-4 border-t pt-6 sm:flex-row sm:items-center">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <p className="text-rl-text3 text-[0.7rem]">
              © {year} Raileon LLC. All rights reserved.
            </p>
            {/* Secondary legal strip. These four belong in the Legal column by
                topic, but putting all nine there unbalances the 5-column grid
                — so the ones a visitor looks up deliberately (rather than
                browses) live down here instead. */}
            {SECONDARY_LEGAL.map((l) => (
              <Link
                key={l.href}
                href={l.href}
                className="text-rl-text3 hover:text-rl-text2 text-[0.7rem] transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </div>

          <p className="text-rl-text3 text-[0.7rem] italic">
            Built by humans. Scaled by agents.
          </p>
        </div>
      </div>
      <LeadCaptureModal open={demoOpen} onOpenChange={setDemoOpen} />
    </footer>
  );
}
